import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface Permission {
  permission_key: string;
  permission_name: string;
  description: string;
  category: string;
  is_system_permission: boolean;
}

export interface PermissionProfile {
  id: string;
  tenant_id: string | null;
  platform_admin_id: string | null;
  profile_name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  permissions?: string[];
}

export interface UserProfileAssignment {
  id: string;
  user_id: string;
  profile_id: string;
  assigned_by: string;
  assigned_at: string;
  is_active: boolean;
  profile?: PermissionProfile;
  user?: {
    id: string;
    full_name: string;
    email: string;
    role: string;
  };
}

export const usePermissions = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-permissions', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase.rpc('get_user_permissions');

      if (error) {
        if (import.meta.env.DEV) console.error('[usePermissions] RPC error:', error.message, error);
        throw error;
      }

      if (import.meta.env.DEV) console.log('[usePermissions] RPC returned', data?.length ?? 0, 'permissions:', data);
      return data || [];
    },
    enabled: !!user,
  });
};

export const useHasPermission = (permissionKey: string) => {
  const { data: permissions = [] } = usePermissions();
  return permissions.includes(permissionKey);
};

export const usePermissionDefinitions = () => {
  return useQuery({
    queryKey: ['permission-definitions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('permission_definitions')
        .select('*')
        .order('category', { ascending: true })
        .order('permission_name', { ascending: true });
      
      if (error) throw error;
      return data as Permission[];
    },
  });
};

export const usePermissionProfiles = (tenantId?: string) => {
  return useQuery({
    queryKey: ['permission-profiles', tenantId],
    queryFn: async () => {
      let query = supabase
        .from('permission_profiles')
        .select(`
          *,
          profile_permissions!inner(permission_key)
        `)
        .eq('is_active', true)
        .order('profile_name');

      if (tenantId) {
        query = query.eq('tenant_id', tenantId);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      // Transform the data to include permissions array
      return data?.map(profile => ({
        ...profile,
        permissions: profile.profile_permissions?.map((pp: { permission_key?: string }) => pp.permission_key) || []
      })) as PermissionProfile[];
    },
    enabled: !!tenantId,
  });
};

export const useCreatePermissionProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      tenantId, 
      profileName, 
      description, 
      permissions 
    }: {
      tenantId: string;
      profileName: string;
      description?: string;
      permissions: string[];
    }) => {
      // Create the profile
      const { data: profile, error: profileError } = await supabase
        .from('permission_profiles')
        .insert({
          tenant_id: tenantId,
          profile_name: profileName,
          description: description,
        })
        .select()
        .single();

      if (profileError) throw profileError;

      // Add permissions to the profile
      if (permissions.length > 0) {
        const { error: permissionsError } = await supabase
          .from('profile_permissions')
          .insert(
            permissions.map(permissionKey => ({
              profile_id: profile.id,
              permission_key: permissionKey,
            }))
          );

        if (permissionsError) throw permissionsError;
      }

      return profile;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['permission-profiles'] });
      toast.success('Permission profile created successfully');
    },
    onError: (error: unknown) => {
      console.error('Error creating permission profile:', error);
      toast.error('Failed to create permission profile');
    },
  });
};

export const useUpdatePermissionProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      profileId, 
      profileName, 
      description, 
      permissions 
    }: {
      profileId: string;
      profileName?: string;
      description?: string;
      permissions?: string[];
    }) => {
      // Update profile info if provided
      if (profileName || description) {
        const updateData: Record<string, unknown> = {};
        if (profileName) updateData.profile_name = profileName;
        if (description) updateData.description = description;

        const { error: updateError } = await supabase
          .from('permission_profiles')
          .update(updateData)
          .eq('id', profileId);

        if (updateError) throw updateError;
      }

      // Update permissions if provided
      if (permissions) {
        // Remove existing permissions
        const { error: deleteError } = await supabase
          .from('profile_permissions')
          .delete()
          .eq('profile_id', profileId);

        if (deleteError) throw deleteError;

        // Add new permissions
        if (permissions.length > 0) {
          const { error: insertError } = await supabase
            .from('profile_permissions')
            .insert(
              permissions.map(permissionKey => ({
                profile_id: profileId,
                permission_key: permissionKey,
              }))
            );

          if (insertError) throw insertError;
        }
      }

      return { profileId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-profiles'] });
      toast.success('Permission profile updated successfully');
    },
    onError: (error: unknown) => {
      console.error('Error updating permission profile:', error);
      toast.error('Failed to update permission profile');
    },
  });
};

export const useUserProfileAssignments = (tenantId?: string) => {
  return useQuery({
    queryKey: ['user-profile-assignments', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_profile_assignments')
        .select(`
          id,
          user_id,
          profile_id,
          assigned_by,
          assigned_at,
          is_active,
          permission_profiles!profile_id(
            id,
            profile_name,
            description,
            tenant_id,
            platform_admin_id,
            is_default,
            is_active,
            created_by,
            created_at,
            updated_at
          ),
          users!user_id!inner(
            id,
            full_name,
            email,
            role,
            tenant_id
          )
        `)
        .eq('is_active', true)
        .eq('users.tenant_id', tenantId)
        .order('assigned_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(assignment => ({
        id: assignment.id,
        user_id: assignment.user_id,
        profile_id: assignment.profile_id,
        assigned_by: assignment.assigned_by,
        assigned_at: assignment.assigned_at,
        is_active: assignment.is_active,
        profile: assignment.permission_profiles as PermissionProfile,
        user: assignment.users as {
          id: string;
          full_name: string;
          email: string;
          role: string;
        }
      })) as UserProfileAssignment[];
    },
    enabled: !!tenantId,
  });
};

export const useAssignUserToProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      userId, 
      profileId 
    }: {
      userId: string;
      profileId: string;
    }) => {
      const { data, error } = await supabase
        .from('user_profile_assignments')
        .insert({
          user_id: userId,
          profile_id: profileId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
      toast.success('User assigned to profile successfully');
    },
    onError: (error: unknown) => {
      console.error('Error assigning user to profile:', error);
      toast.error('Failed to assign user to profile');
    },
  });
};

export const useRemoveUserFromProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      userId, 
      profileId 
    }: {
      userId: string;
      profileId: string;
    }) => {
      const { error } = await supabase
        .from('user_profile_assignments')
        .update({ is_active: false })
        .eq('user_id', userId)
        .eq('profile_id', profileId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
      toast.success('User removed from profile successfully');
    },
    onError: (error: unknown) => {
      console.error('Error removing user from profile:', error);
      toast.error('Failed to remove user from profile');
    },
  });
};

// User Management Hooks - Enable/Disable/Delete Users
export function useDisableUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, tenantId }: { userId: string; tenantId: string }) => {
      const { data, error } = await supabase.rpc('disable_tenant_user', {
        p_user_id: userId,
        p_tenant_id: tenantId,
      });
      
      if (error) throw error;
      const result = data as { success: boolean; error?: string; message?: string };
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
      toast.success(data.message || 'User disabled successfully');
    },
    onError: (error: unknown) => {
      console.error('Error disabling user:', error);
      toast.error(error.message || 'Failed to disable user');
    },
  });
}

export function useEnableUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, tenantId }: { userId: string; tenantId: string }) => {
      const { data, error } = await supabase.rpc('enable_tenant_user', {
        p_user_id: userId,
        p_tenant_id: tenantId,
      });
      
      if (error) throw error;
      const result = data as { success: boolean; error?: string; message?: string };
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
      toast.success(data.message || 'User enabled successfully');
    },
    onError: (error: unknown) => {
      console.error('Error enabling user:', error);
      toast.error(error.message || 'Failed to enable user');
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, tenantId }: { userId: string; tenantId: string }) => {
      const { data, error } = await supabase.rpc('delete_tenant_user', {
        p_user_id: userId,
        p_tenant_id: tenantId,
      });
      
      if (error) throw error;
      const result = data as { success: boolean; error?: string; message?: string };
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
      toast.success(data.message || 'User deleted successfully');
    },
    onError: (error: unknown) => {
      console.error('Error deleting user:', error);
      toast.error(error.message || 'Failed to delete user');
    },
  });
};